import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import {
    BlueprintEmailConfigurationType,
    IBlueprintEmailConfiguration,
    IBlueprintEmailConfigurationDAO,
} from '../../../../../api/blueprint-email-configuration.dao';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../infrastructure/sub-windows';
import { handleApiException, showNotification } from '../../../../global/modal/alerts-host/alert-templates';
import { ConfigureOrderBookingEmail } from './configure-order-booking-email';
import {
    CONFIGURE_EMAIL_TITLE,
    COORDINATOR_BOOKING_CREATED_LABEL,
    EMAIL_UPDATED_NOTIFICATION,
} from './order-booking-emails.texts';
import { LONG_RICH_TEXT_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../constants/common.constants';
import { calcByteCodeSize } from '../../../../../infrastructure/utils/common.utils';

class OrderBookingEmailsListVM {
    @mobx.observable
    public loading = false;

    @mobx.observable
    public configurations: IBlueprintEmailConfiguration[] = [];

    @mobx.observable
    private configureEmailPopup: ISubWindowFacade;

    constructor(
        private readonly accountId: number,
        private readonly blueprintId: string,
        private readonly intl: IntlShape,
        private readonly emailConfigurationDAO: IBlueprintEmailConfigurationDAO =
            IoC.Container.get(IBlueprintEmailConfigurationDAO),
        private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
    ) {
    }

    public async load(): Promise<void> {
        mobx.runInAction(() => {
            this.loading = true;
        });
        try {
            const response = await this.emailConfigurationDAO.getEmailConfigurations({
                accountId: this.accountId,
                blueprintId: this.blueprintId,
            });
            let configurations = response?.configurations || [];
            if (!configurations.length) {
                const defaults = await this.emailConfigurationDAO.getDefaultEmailConfigurations({
                    accountId: this.accountId,
                    blueprintId: this.blueprintId,
                });
                configurations = defaults?.configurations || [];
            }
            mobx.runInAction(() => {
                this.configurations = configurations;
            });
        } catch (error) {
            handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    }

    public getEmailLabel(configuration: IBlueprintEmailConfiguration): string {
        return configuration.name
            || this.intl.formatMessage(COORDINATOR_BOOKING_CREATED_LABEL);
    }

    @mobx.action
    public showConfigureEmail = (configuration: IBlueprintEmailConfiguration) => {
        if (
            !this.configureEmailPopup
            || this.configureEmailPopup.getStatus() === SubWindowStatus.Closed
        ) {
            const configureVm = new ConfigureOrderBookingEmailVM(
                this.accountId,
                this.blueprintId,
                configuration,
                this.intl,
                async () => {
                    await this.load();
                },
                () => {
                    this.configureEmailPopup?.close();
                },
            );
            this.configureEmailPopup = this.subWindowService.showSubWindow({
                componentToHost: ConfigureOrderBookingEmail,
                componentToHostProps: { vm: configureVm },
                title: this.intl.formatMessage(CONFIGURE_EMAIL_TITLE),
            });
        }
    };
}

class ConfigureOrderBookingEmailVM {
    @mobx.observable
    public subject: string;

    @mobx.observable
    public body: string;

    @mobx.observable
    public saving = false;

    @mobx.observable
    public subjectError = '';

    @mobx.observable
    public bodyError = '';

    private readonly originalType: string;

    constructor(
        private readonly accountId: number,
        private readonly blueprintId: string,
        configuration: IBlueprintEmailConfiguration,
        private readonly intl: IntlShape,
        private readonly onSaved: () => Promise<void>,
        private readonly onClose: () => void,
        private readonly emailConfigurationDAO: IBlueprintEmailConfigurationDAO =
            IoC.Container.get(IBlueprintEmailConfigurationDAO),
    ) {
        this.subject = configuration.subject || '';
        this.body = configuration.body || '';
        this.originalType = configuration.type || BlueprintEmailConfigurationType.COORDINATOR_BOOKING_CREATED;
    }

    @mobx.action
    public setSubject = (value: string) => {
        this.subject = value;
        this.subjectError = '';
    };

    @mobx.action
    public setBody = (value: string) => {
        this.body = value;
        this.bodyError = '';
    };

    @mobx.action
    public cancel = () => {
        this.onClose();
    };

    public apply = async () => {
        if (!this.validate()) {
            return;
        }
        mobx.runInAction(() => {
            this.saving = true;
        });
        try {
            await this.emailConfigurationDAO.updateEmailConfigurations({
                accountId: this.accountId,
                blueprintId: this.blueprintId,
                configurations: [{
                    type: this.originalType,
                    subject: this.subject,
                    body: this.body,
                    name: this.intl.formatMessage(COORDINATOR_BOOKING_CREATED_LABEL),
                }],
            });
            showNotification(this.intl.formatMessage(EMAIL_UPDATED_NOTIFICATION));
            await this.onSaved();
            this.onClose();
        } catch (error) {
            handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    private validate(): boolean {
        let valid = true;
        if (!this.subject.trim()) {
            this.subjectError = 'Email Subject is required.';
            valid = false;
        } else if (calcByteCodeSize(this.subject) > SHORT_PLAIN_TEXT_LENGTH) {
            this.subjectError = 'Email Subject is too long.';
            valid = false;
        }
        if (!this.body || !this.body.replace(/<[^>]*>/g, '').trim()) {
            this.bodyError = 'Email Body is required.';
            valid = false;
        } else if (calcByteCodeSize(this.body) > LONG_RICH_TEXT_LENGTH) {
            this.bodyError = 'Email Body is too long.';
            valid = false;
        }
        return valid;
    }
}

export { OrderBookingEmailsListVM, ConfigureOrderBookingEmailVM };
